import {
  PwsMediaDeliveryNextQuery,
  PwsMediaDeliveryVideosSearchQuery,
  PwsMediaVideoFragmentFragment,
  GetPwsMediaConfigQuery,
  PwsMediaDeliveryVideosBySlugsResponse,
  PwsMediaDeliveryVideosResponse,
} from "@ihr-radioedit/inferno-webapi";
import axios, { AxiosInstance } from "axios";
import { format } from "date-fns";
import {
  PwsMediaEpisode,
  PwsMediaConfigResponse,
  PwsMediaConfigTab,
  PwsOnAirStatusResponse,
  PwsViewMediaEpisodeResponse,
  PwsListMediaEpisodesResponse,
  PwsListMediaEpisodesParams,
  PwsMediaIsAudioResponse,
  ZypePagination,
  PwsPremiereMediaResponseWrapper,
  PwsPremiereMediaListResponseWrapper,
  PwsPremiereMediaResponseData,
} from "./types";
import { coastMediaEpisodeCategoriesToGroupSlug } from "./util";
import { PWSGraphQLServiceBase } from "./base-graphql";

const transformVideo = (video: PwsMediaVideoFragmentFragment | null | undefined): PwsMediaEpisode | null => {
  if (!video) return null;

  return {
    id: video.id ?? "",
    title: video.title,
    slug: video.slug,
    description: video.description ?? "",
    shortDescription: video.shortDescription ?? "",
    thumbnails: (video.thumbnails ?? []).filter((t): t is NonNullable<typeof t> => t !== null),
    categories: (video.categories ?? [])
      .filter((c): c is NonNullable<typeof c> => c !== null)
      .map(c => ({ ...c, value: (c.value ?? []).filter((v): v is string => v !== null) })),
    publishedAt: video.publishedAt ?? "",
    subscriptionRequired: video.subscriptionRequired,
    manifest: video.manifest ?? null,
    onAir: video.onAir ?? false,
    duration: video.duration ?? 0,
    isAudio: video.isAudio ?? null,
    autoplay: video.autoplay,
  };
};

type GraphQLConfigTab = GetPwsMediaConfigQuery["pws"]["mediaDelivery"]["config"]["tabs"][number];

const transformConfigTab = (tab: GraphQLConfigTab): PwsMediaConfigTab => {
  return {
    slug: tab.slug,
    title: tab.title,
    hasLive: tab.hasLive,
    autoplay: tab.autoplay,
    videos: tab.videos?.map(transformVideo).filter((v): v is PwsMediaEpisode => v !== null) ?? null,
    categories: tab.categories ?? {},
    pagination: tab.pagination
      ? {
          current: tab.pagination.current,
          previous: tab.pagination.previous ?? null,
          next: tab.pagination.next ?? null,
          per_page: tab.pagination.per_page,
          pages: tab.pagination.pages,
        }
      : undefined,
    resume: tab.resume ?? null,
  };
};

export class PWSMediaGraphQLService extends PWSGraphQLServiceBase {
  private premiereMediaApi: AxiosInstance;

  constructor(host: string, siteSlug: string, appToken?: string, pwsHost?: string) {
    super(host, siteSlug, appToken, "PWS GraphQL Service", pwsHost);

    this.premiereMediaApi = axios.create({
      baseURL: `${this.protocol}://${this.pwsHost}/premieremedia/streamlink`,
    });
  }

  async getOnAirStatus(): Promise<PwsOnAirStatusResponse> {
    return this.withLogging("getOnAirStatus", async () => {
      const result = await this.sdk.GetPWSOnAirStatus({ show: this.show });
      const response = result.pws?.mediaDelivery?.isOnAir;

      return {
        isOnAir: response?.isOnAir ?? false,
      };
    });
  }

  async getOnAirEpisode(accessToken: string | null): Promise<PwsViewMediaEpisodeResponse> {
    return this.withLogging("getOnAirEpisode", async () => {
      const headers = this.getAuthHeaders(accessToken);

      const result = await this.sdk.GetPWSOnAirVideo({ show: this.show }, headers);
      const response = result.pws?.mediaDelivery?.video?.onAir;

      return {
        authenticated: response?.authenticated ?? false,
        playerApiKey: response?.playerApiKey ?? "",
        video: transformVideo(response?.video),
      };
    });
  }

  async getMediaConfig(
    accessToken: string | null,
    includeVideos = true,
    perPage = 12,
  ): Promise<PwsMediaConfigResponse> {
    return this.withLogging("getMediaConfig", async () => {
      const headers = this.getAuthHeaders(accessToken);

      const result = await this.sdk.GetPWSMediaConfig(
        {
          show: this.show,
          includeVideos,
          perPage,
        },
        headers,
      );
      const response = result.pws?.mediaDelivery?.config;

      return {
        authenticated: response?.authenticated ?? false,
        playerApiKey: response?.playerApiKey ?? "",
        tabs: response?.tabs?.map(transformConfigTab) ?? [],
      };
    });
  }

  async getIsAudioEpisode(accessToken: string | null, id: string, onAir: boolean): Promise<PwsMediaIsAudioResponse> {
    return this.withLogging("getIsAudioEpisode", async () => {
      const headers = this.getAuthHeaders(accessToken);

      const result = await this.sdk.GetPWSIsAudioVideo(
        {
          show: this.show,
          id,
          isOnAir: onAir,
        },
        headers,
      );
      const response = result.pws?.mediaDelivery?.video?.isAudio;

      return {
        authenticated: response?.authenticated ?? false,
        playerApiKey: response?.playerApiKey ?? "",
        isAudio: response?.isAudio ?? false,
      };
    });
  }

  async getNextEpisode(curEpisode: PwsMediaEpisode, accessToken: string | null): Promise<PwsViewMediaEpisodeResponse> {
    return this.withLogging("getNextEpisode", async () => {
      const headers = this.getAuthHeaders(accessToken);
      const groupSlug = coastMediaEpisodeCategoriesToGroupSlug(curEpisode.categories);

      const query: PwsMediaDeliveryNextQuery = {
        publishedAt: curEpisode.publishedAt,
        groupSlug,
      };

      const result = await this.sdk.GetPWSNextVideo(
        {
          show: this.show,
          query,
        },
        headers,
      );
      const response = result.pws?.mediaDelivery?.video?.next;

      return {
        authenticated: response?.authenticated ?? false,
        playerApiKey: response?.playerApiKey ?? "",
        video: transformVideo(response?.video),
      };
    });
  }

  async getMediaEpisodeBySlug(slug: string, accessToken?: string | null): Promise<PwsViewMediaEpisodeResponse> {
    if (slug === "live") {
      return this.getOnAirEpisode(accessToken ?? null);
    }

    return this.withLogging("getMediaEpisodeBySlug", async () => {
      const headers = this.getAuthHeaders(accessToken);

      const result = await this.sdk.GetPWSVideoBySlug(
        {
          show: this.show,
          slug,
        },
        headers,
      );
      const response = result.pws?.mediaDelivery?.video?.bySlug;

      return {
        authenticated: response?.authenticated ?? false,
        playerApiKey: response?.playerApiKey ?? "",
        video: transformVideo(response?.video),
      };
    });
  }

  async getMediaEpisodeBySlugBulk(
    slugs: string[],
    accessToken?: string | null,
  ): Promise<Record<string, PwsViewMediaEpisodeResponse>> {
    const results = await Promise.all(
      slugs.map(slug =>
        this.getMediaEpisodeBySlug(slug, accessToken).catch(e => {
          this.log.error(e);
          return null;
        }),
      ),
    );

    return results.reduce((acc, cur, idx) => {
      if (cur && cur.video) {
        acc[slugs[idx]] = cur;
      }
      return acc;
    }, {} as Record<string, PwsViewMediaEpisodeResponse>);
  }

  async getMediaEpisodesBySlugs(slugs: string[], accessToken?: string): Promise<PwsListMediaEpisodesResponse> {
    return this.withLogging("getMediaEpisodesBySlugs", async () => {
      const headers = this.getAuthHeaders(accessToken);

      const result = await this.sdk.GetPWSVideosBySlugs(
        {
          show: this.show,
          slugs,
        },
        headers,
      );
      const response = result.pws?.mediaDelivery?.videos?.bySlugs as PwsMediaDeliveryVideosBySlugsResponse;

      return {
        authenticated: response?.authenticated ?? false,
        playerApiKey: response?.playerApiKey ?? "",
        videos: response?.videos?.map(transformVideo).filter((v): v is PwsMediaEpisode => v !== null) ?? [],
        pagination: {
          current: 1,
          previous: null,
          next: null,
          per_page: slugs.length,
          pages: 1,
        },
        resume: null,
      };
    });
  }

  async listMediaEpisodes(
    resume: string | null = null,
    params: PwsListMediaEpisodesParams = {},
    accessToken?: string,
  ): Promise<PwsListMediaEpisodesResponse> {
    return this.withLogging("listMediaEpisodes", async () => {
      const headers = this.getAuthHeaders(accessToken);
      const searchQuery: PwsMediaDeliveryVideosSearchQuery = {};

      if (params.groupSlug) {
        searchQuery.groupSlug = params.groupSlug;
      }
      if (params.categoryName) {
        searchQuery.categoryName = params.categoryName;
      }
      if (params.categoryValue) {
        searchQuery.categoryValue = params.categoryValue;
      }
      if (params.query) {
        searchQuery.query = params.query;
      }
      if (params.startDate) {
        searchQuery.startDate = params.startDate;
      }
      if (params.endDate) {
        searchQuery.endDate = params.endDate;
      }
      if (params.categories) {
        searchQuery.categories = JSON.stringify(params.categories);
      }

      const result = await this.sdk.GetPWSVideosList(
        {
          show: this.show,
          page: params.page,
          perPage: params.perPage,
          resume: resume ?? undefined,
          query: Object.keys(searchQuery).length > 0 ? searchQuery : undefined,
        },
        headers,
      );
      const response = result.pws?.mediaDelivery?.videos?.list as PwsMediaDeliveryVideosResponse;

      const pagination: ZypePagination = {
        current: response?.pagination?.current ?? 1,
        previous: response?.pagination?.previous ?? null,
        next: response?.pagination?.next ?? null,
        per_page: response?.pagination?.per_page ?? params.perPage ?? 12,
        pages: response?.pagination?.pages ?? 1,
      };

      return {
        authenticated: response?.authenticated ?? false,
        playerApiKey: response?.playerApiKey ?? "",
        videos: response?.videos?.map(transformVideo).filter((v): v is PwsMediaEpisode => v !== null) ?? [],
        pagination,
        resume: response?.resume ?? null,
      };
    });
  }

  async getMP3Link(showIdentifier: string, clip: number, accessToken: string): Promise<PwsPremiereMediaResponseData> {
    const {
      data: {
        data: { data: res },
      },
    } = await this.premiereMediaApi.get<PwsPremiereMediaResponseWrapper>(
      `/coast/show/${encodeURIComponent(showIdentifier)}/${encodeURIComponent(clip)}`,
      {
        headers: this.getCommonHeaders(accessToken),
      },
    );

    return res;
  }

  private getShowIdentifier(showDate: Date | number) {
    return format(showDate, "yyyy-MM-dd");
  }

  async getMP3LinksBulk(showDate: Date | number, accessToken: string): Promise<Record<string, string>> {
    const showIdentifier = this.getShowIdentifier(showDate);

    return (
      await Promise.all(
        [1, 2, 3, 4].map(hour => this.getMP3Link(showIdentifier, hour, accessToken).catch(e => this.log.error(e))),
      )
    ).reduce((acc, cur) => {
      if (cur) {
        acc[cur.clip] = cur.url;
      }
      return acc;
    }, {} as Record<string, string>);
  }

  async getWMPLink(
    type: string,
    showIdentifier: string,
    session: { accessToken?: string; hasActiveSubscription?: boolean },
  ): Promise<PwsPremiereMediaResponseData | null> {
    if (!session.accessToken || !session.hasActiveSubscription) {
      return null;
    }

    const {
      data: {
        data: { data: res },
      },
    } = await this.premiereMediaApi.get<PwsPremiereMediaResponseWrapper>(
      `/coast_win/${encodeURIComponent(type)}/${encodeURIComponent(showIdentifier)}`,
      {
        headers: this.getCommonHeaders(session.accessToken),
      },
    );

    return res;
  }

  async getMPWLinksBulk(
    types: string[],
    showDate: Date | number,
    session: { accessToken?: string; hasActiveSubscription?: boolean },
  ): Promise<Record<string, string>> {
    const showIdentifier = this.getShowIdentifier(showDate);

    return (
      await Promise.all(types.map(type => this.getWMPLink(type, showIdentifier, session).catch(e => this.log.error(e))))
    ).reduce((acc, cur) => {
      if (cur) {
        acc[cur.type] = cur.url;
      }
      return acc;
    }, {} as Record<string, string>);
  }

  async listHighlightsLinks(ids: string[]): Promise<PwsPremiereMediaResponseData[]> {
    const {
      data: {
        data: {
          data: { media },
        },
      },
    } = await this.premiereMediaApi.get<PwsPremiereMediaListResponseWrapper>(
      `/list/coast_highlights/show/${ids.map(encodeURIComponent).join(",")}`,
      {
        params: { includeMetadata: 1 },
      },
    );

    return media;
  }
}
